// Client-side upload pre-validation. Runs before bytes leave the browser so
// the user gets fast feedback (file too big, wrong type) without waiting for
// the worker to refuse the upload. The server runs the same checks plus a
// magic-byte sniff and is the only authority — never trust this layer for
// security, only for UX.
//
// Returns i18n keys instead of formatted strings so the UI can localise the
// error. Pair the key with the `params` object when calling t().
//
// Spec: docs/specs/f8m-security-spec.md (upload pipeline, client tier)

// ─────────────────────────────────────────────────────────────────────────────
// Constants — keep in sync with worker/src/services/validation.service.ts
// ─────────────────────────────────────────────────────────────────────────────

const MB = 1024 * 1024;

const SIZE_LIMITS = {
  // Media: images cap at 25MB, videos at 100MB. We use the larger cap here
  // because the category alone doesn't tell us which one — the worker
  // re-checks per-extension and can still reject an oversized image.
  media:    { image: 25 * MB, video: 100 * MB },
  document: 10 * MB,
  asset:    5 * MB,
};

const MEDIA_IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'gif'];
const MEDIA_VIDEO_EXTS = ['mp4', 'mov', 'webm'];
const DOCUMENT_EXTS    = ['pdf', 'eml'];

const ALLOWED_EXTS = {
  media:    [...MEDIA_IMAGE_EXTS, ...MEDIA_VIDEO_EXTS],
  document: DOCUMENT_EXTS,
  asset:    MEDIA_IMAGE_EXTS, // logos and brand graphics are still images
};

const ALLOWED_MIMES = {
  media: new Set([
    'image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/gif',
    'video/mp4', 'video/quicktime', 'video/webm',
  ]),
  document: new Set([
    'application/pdf', 'message/rfc822', 'text/plain',
  ]),
  asset: new Set([
    'image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/gif',
  ]),
};

// Always blocked, regardless of category — mirrors the server list.
const EXECUTABLE_EXTS = new Set([
  'exe', 'bat', 'cmd', 'sh', 'ps1', 'msi', 'dll', 'com', 'scr',
  'js',  'vbs', 'wsf', 'jar', 'py',  'rb',  'php',
]);

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function extensionOf(filename) {
  if (typeof filename !== 'string') return '';
  const dot = filename.lastIndexOf('.');
  return dot > 0 && dot < filename.length - 1 ? filename.slice(dot + 1).toLowerCase() : '';
}

function sizeLimitFor(category, ext) {
  if (category === 'document') return SIZE_LIMITS.document;
  if (category === 'asset')    return SIZE_LIMITS.asset;
  // category === 'media'
  return MEDIA_VIDEO_EXTS.includes(ext) ? SIZE_LIMITS.media.video : SIZE_LIMITS.media.image;
}

function formatLimit(bytes) {
  return `${Math.floor(bytes / MB)}MB`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

// Validate a `File` (from <input type="file"> or drag-drop) against a category.
// Returns:
//   { valid: true }
//   { valid: false, errorKey: 'upload.errors.<key>', params: {...} }
export function validateFile(file, category) {
  if (!file || typeof file.size !== 'number' || typeof file.name !== 'string') {
    return { valid: false, errorKey: 'upload.errors.fileCorrupt', params: {} };
  }

  if (!ALLOWED_EXTS[category]) {
    return { valid: false, errorKey: 'upload.errors.fileTypeNotAllowed', params: { types: '' } };
  }

  const ext = extensionOf(file.name);

  // Block executables before anything else — even if the category would have
  // rejected them, we want a clearer message.
  if (EXECUTABLE_EXTS.has(ext)) {
    return { valid: false, errorKey: 'upload.errors.executableBlocked', params: {} };
  }

  // Extension whitelist.
  if (!ext || !ALLOWED_EXTS[category].includes(ext)) {
    return {
      valid: false,
      errorKey: 'upload.errors.fileTypeNotAllowed',
      params: { types: ALLOWED_EXTS[category].map((e) => `.${e}`).join(', ') },
    };
  }

  // MIME whitelist. Some browsers leave file.type empty (e.g. for .heic on
  // older Safari) — in that case we trust the extension and let the server
  // do the magic-byte check.
  if (file.type && !ALLOWED_MIMES[category].has(file.type)) {
    return {
      valid: false,
      errorKey: 'upload.errors.fileTypeNotAllowed',
      params: { types: ALLOWED_EXTS[category].map((e) => `.${e}`).join(', ') },
    };
  }

  // Size cap.
  const maxBytes = sizeLimitFor(category, ext);
  if (file.size > maxBytes) {
    return {
      valid: false,
      errorKey: 'upload.errors.fileTooLarge',
      params: { limit: formatLimit(maxBytes), category },
    };
  }

  return { valid: true };
}

// Convenience for batch validation — returns the first invalid result, or
// { valid: true } if everything passes.
export function validateFiles(files, category) {
  for (const file of files) {
    const result = validateFile(file, category);
    if (!result.valid) return result;
  }
  return { valid: true };
}

// Re-exported so call sites can show the right limit in placeholder text.
export const FILE_LIMITS = {
  imageMb:    Math.floor(SIZE_LIMITS.media.image / MB),
  videoMb:    Math.floor(SIZE_LIMITS.media.video / MB),
  documentMb: Math.floor(SIZE_LIMITS.document / MB),
  assetMb:    Math.floor(SIZE_LIMITS.asset / MB),
};
